import React, { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Button, Select } from "antd"

import AllScripts from "../scripts/AllScripts"
import SharedScripts from "../scripts/SharedScript"
import Invite from "../scripts/Invite"
import MyProfile from "../MyProfile"
import SearchBar from "../scripts/SearchBar"
import CreateScriptModal from "../Modals/CreateScriptModals"
import ScriptInfoModals from "../Modals/CreateScriptModals/ScriptInfoModals"
import { setCreateSciptOpen, setDashboardMode } from "../../store/navSlice"
import {
  MdDescription,
  MdPeople,
  MdPersonAdd,
  MdPerson,
  MdAdd,
  MdOutlineTune,
  MdAdminPanelSettings,
  MdHelpOutline,
} from "react-icons/md"
import { MdAutoAwesome as MdAI } from "react-icons/md"
import { isAdmin, isStaff } from "../../utils/access"
import ProductionPrototype from "../Admin/ProductionPrototype"

import AIWorkspace from "./AIWorkspace"
import TrialCountdown from "./TrialCountdown"
import FAQ from "./faq/FAQ"
import { loadUserScripts } from "./loadUserScripts"
import { organisationType, organisationLabel } from "./accountDashboard"
import { organisationRequest } from "./organisationApi"
import { getAccessToken } from '../../services/authService'
import OrganisationSettings from "./OrganisationSettings"
import TeamMembersPreview from "./TeamMembersPreview"
import SubscriptionPreview from "../MyProfile/Subscription/SubscriptionPreview"

const sections = [
  { id: "scripts", title: "My Scripts", icon: MdDescription },
  { id: "shared", title: "Shared With Me", icon: MdPeople },
  { id: "invite", title: "Invite", icon: MdPersonAdd },
  { id: "profile", title: "Profile", icon: MdPerson },
  { id: "faq", title: "FAQ", icon: MdHelpOutline },
  { id: "ai", title: "AI", icon: MdAI },
]

// Admin-only review; regular users keep Production disabled.
const modes = [
  { id: "screenwriting", title: "Screenwriting" },
  { id: "production", title: "Production", comingSoon: true },
]

const UserDashboard = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((s) => s.auth.user) || JSON.parse(localStorage.getItem("user") || "null")
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.name || ""
  const company = Boolean(organisationType(user))
  const [organisation, setOrganisation] = useState(null)
  const [teamAccess, setTeamAccess] = useState(null)
  useEffect(() => {
    let active = true
    fetch(`${process.env.REACT_APP_DEV_API}/account/organisation/access`, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
      .then(r => r.ok ? r.json() : null).then(body => { if (active) setTeamAccess(body?.data || null) }).catch(() => {})
    return () => { active = false }
  }, [user?._id])
  const dashboardSections = company ? [
    sections[0], { id: "subscription", title: "Subscription", icon: MdPerson },
    { id: "team", title: "Team Members", icon: MdPeople }, sections.find(item => item.id === "ai"),
    { id: "settings", title: user.accountType === "ADVERTISING_AGENCY" ? "Agency Settings" : "Company Settings", icon: MdOutlineTune },
  ] : teamAccess?.manager ? [...sections, { id: 'team', title: 'Company Team', icon: MdPeople }] : sections
  useEffect(() => {
    setOrganisation(null)
    if (!company) return
    let active = true
    organisationRequest().then(next => { if (active) setOrganisation(next) }).catch(() => {})
    return () => { active = false }
  }, [company, user?._id])
  const location = useLocation()
  const [section, setSection] = useState(location.state?.section === "shared" ? "shared" : "scripts")
  const [mode, setMode] = useState(location.state?.mode === "production" ? "production" : "screenwriting")
  const canReviewProduction = isAdmin(user) && isStaff(user)
  const [searchVal, setSearchVal] = useState("")
  const [showFilterModal, setShowFilterModal] = useState(false)
  const [showCreateScriptModal, setShowCreateScriptModal] = useState(false)
  const [createType, setCreateType] = useState("")
  const [inviteScriptId, setInviteScriptId] = useState(null)
  const [inviteScripts, setInviteScripts] = useState([])
  const [scriptsLoading, setScriptsLoading] = useState(false)
  const [scriptsError, setScriptsError] = useState(false)
  const [selectorRetry, setSelectorRetry] = useState(0)
  const isScriptSection = section === "scripts" || section === "shared"
  // The mobile bottom bar's "Scripts" shortcut arrives with state.section — follow it
  useEffect(() => {
    const next = location.state?.section
    if (dashboardSections.some((item) => item.id === next)) setSection(next)
  }, [location.state?.section])
  // The mobile Production shortcut opens the most recent production overview directly.
  const autoOpenRef = useRef(location.state?.openProduction)
  const [openRequest, setOpenRequest] = useState(0)
  // The mobile Schedule shortcut targets the Shooting Schedule section instead of the Overview.
  const [autoSection, setAutoSection] = useState(undefined)
  useEffect(() => {
    if (location.state?.mode === "screenwriting") {
      // Bottom-bar Dashboard: close Production Overview and show the main Dashboard.
      // Keep a requested section (Script → My Scripts) so the bottom bar can highlight it.
      setMode("screenwriting")
      const keptSection = location.state?.section
      navigate(location.pathname, { replace: true, state: keptSection ? { section: keptSection } : {} })
      return
    }
    if (location.state?.mode !== "production") return
    const nextOpen = location.state?.openProduction
    if (nextOpen) autoOpenRef.current = nextOpen
    setAutoSection(location.state?.openSection === "schedule" ? "schedule" : undefined)
    // A repeat shortcut while Production is already open re-opens the latest production.
    // Keeps an already-open production (no remount); otherwise opens the latest one.
    setOpenRequest((n) => n + 1)
    setMode("production")
    navigate(location.pathname, { replace: true, state: {} })
  }, [location.state?.mode, location.state?.openProduction, location.state?.openSection])
  // Keep the shortcut highlight after its one-time navigation state is consumed.
  useEffect(() => {
    dispatch(setDashboardMode(mode === "production" && canReviewProduction
      ? (autoSection === "schedule" ? "schedule" : "production")
      : "screenwriting"))
  }, [mode, canReviewProduction, autoSection])
  useEffect(() => () => { dispatch(setDashboardMode(null)) }, [])
  useEffect(() => {
    if (section !== "invite") return
    let cancelled = false
    const loadScripts = async () => {
      setScriptsLoading(true)
      setScriptsError(false)
      try {
        const scripts = await loadUserScripts(() => cancelled)
        if (!cancelled) setInviteScripts(scripts)
      } catch (error) {
        if (!cancelled) setScriptsError(true)
      } finally {
        if (!cancelled) setScriptsLoading(false)
      }
    }
    loadScripts()
    return () => { cancelled = true }
  }, [section, selectorRetry])
  const activeSection = dashboardSections.find((item) => item.id === section)
  const openCreate = () => {
    if (window.innerWidth < 1024) dispatch(setCreateSciptOpen(true))
    else setShowCreateScriptModal(true)
  }
  // Trial banner: open the existing Profile → Subscription section in place.
  const openProfileSubscription = () => {
    setSection("profile")
    navigate(location.pathname, { replace: true, state: { section: "profile", profileSection: "subscription" } })
  }
  // Signed in from an invitation link: return to that invitation so it can be accepted
  useEffect(() => {
    const inviteUrl = localStorage.getItem("invite_url")
    if (inviteUrl && inviteUrl.startsWith("/invite/")) {
      localStorage.removeItem("invite_url")
      navigate(inviteUrl, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // New user after email verification: open the existing Film / TV / Treatment picker once
  useEffect(() => {
    if (location.state?.openCreateScript === true) {
      openCreate()
      navigate(location.pathname, { replace: true, state: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Mobile: dropdowns (e.g. AI Script Analysis) must not open the phone keyboard when tapped
  useEffect(() => {
    const root = document.querySelector(".morphosys-dashboard")
    if (!root) return
    const apply = () => root.querySelectorAll(".ant-select-selection-search-input").forEach((el) => {
      if (el.getAttribute("inputmode") !== "none") el.setAttribute("inputmode", "none")
    })
    apply()
    const observer = new MutationObserver(apply)
    observer.observe(root, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  return (
    <div className="morphosys-dashboard h-full overflow-y-auto">
      <style>{`
        .morphosys-dashboard { --dashboard-primary: #0099FF; --dashboard-ink: #292929; --dashboard-muted: #868686; --dashboard-border: #E4E4E4; --dashboard-surface: #FFFFFF; --dashboard-bg: #FBFBFB; --dashboard-tint: #EAF6FF; background: var(--dashboard-bg); color: var(--dashboard-ink); }
        .morphosys-dashboard .dashboard-muted { color: var(--dashboard-muted); }
        .morphosys-dashboard .dashboard-nav { border-color: var(--dashboard-border); }
        .morphosys-dashboard .dashboard-nav .ant-btn { color: var(--dashboard-muted); height: 44px; justify-content: flex-start; box-shadow: none; font-family: inherit; }
        .morphosys-dashboard .dashboard-nav .ant-btn[aria-current="page"] { color: var(--dashboard-primary); background: var(--dashboard-tint); font-weight: 700; }
        .morphosys-dashboard .dashboard-primary.ant-btn { background: var(--dashboard-primary); border-color: var(--dashboard-primary); color: var(--dashboard-surface); box-shadow: none; font-family: inherit; }
        .morphosys-dashboard .dashboard-tool.ant-btn { color: var(--dashboard-muted); border-color: var(--dashboard-border); }
        .morphosys-dashboard .dashboard-icon { background: var(--dashboard-tint); color: var(--dashboard-primary); }
        .morphosys-dashboard .dashboard-ai-card { background: var(--dashboard-surface); border-color: var(--dashboard-border); }
        .morphosys-dashboard .dashboard-badge { background: var(--dashboard-bg); color: var(--dashboard-muted); }
        .morphosys-dashboard .dashboard-modes { background: var(--dashboard-surface); border-color: var(--dashboard-border); }
        .morphosys-dashboard .dashboard-mode { background: transparent; border: 0; cursor: pointer; color: var(--dashboard-muted); font-family: inherit; }
        .morphosys-dashboard .dashboard-mode[aria-selected="true"] { background: var(--dashboard-tint); color: var(--dashboard-primary); font-weight: 700; }
        .morphosys-dashboard .dashboard-mode[disabled] { color: var(--dashboard-muted); opacity: 0.55; cursor: not-allowed; }
        .morphosys-dashboard .dashboard-mode[disabled]:hover { background: transparent; }
        .morphosys-dashboard .dashboard-list > div:nth-child(2) > .cursor-pointer { max-width: 100%; }
        @media (min-width: 1024px) {
          .morphosys-dashboard .dashboard-list > div:nth-child(2) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .morphosys-dashboard .dashboard-list > div:nth-child(2) > .cursor-pointer { width: 100%; }
        .morphosys-dashboard .dashboard-list > div:nth-child(2) > :not(.cursor-pointer) { grid-column: 1 / -1; }
        /* Touch devices: stop dropdown taps from opening the phone keyboard */
        @media (pointer: coarse) { .morphosys-dashboard .ant-select-selection-search { pointer-events: none; } }
        }
      `}</style>
      <div className={`max-w-[1440px] mx-auto pb-28 ${mode === "production" ? "p-4 lg:px-8 lg:pt-2" : "p-4 lg:px-8 lg:pt-3 lg:pb-8"}`}>
        <div role="tablist" aria-label="Morphosys mode" className={`dashboard-modes ${mode === "production" ? "hidden lg:flex" : "flex"} w-fit max-w-full flex-wrap gap-1 rounded-full border p-1 ${mode === "production" ? "mb-3 lg:mb-4" : "mb-3 lg:mb-4"}`}>
          {modes.map(({ id, title, comingSoon }) => (
            <Button key={id} type="text" role="tab" aria-selected={mode === id} disabled={comingSoon && !canReviewProduction} title={comingSoon && !canReviewProduction ? "Production is coming soon" : undefined} onClick={() => setMode(id)} className="dashboard-mode h-10 rounded-full px-3 lg:px-4 text-[14px] lg:text-[15px] whitespace-nowrap">
              {title}
              {comingSoon && !canReviewProduction && <span className="dashboard-badge ml-1.5 rounded-full px-1.5 py-[3px] text-[10px] font-bold align-middle">Coming Soon</span>}
            </Button>
          ))}
        </div>
        {mode !== "production" && <header className="mb-4 lg:mb-5">
          <h1 className="text-[24px] lg:text-[28px] font-bold break-words">{company ? organisation?.name || user.companyName || organisationLabel(user) : "Dashboard"}</h1>
          {company && <p className="font-bold mt-1">{organisationLabel(user)} Dashboard</p>}
          <p className="dashboard-muted text-[14px] mt-1">{name ? `Welcome back, ${name}.` : "Welcome back."}</p>
          <TrialCountdown onViewSubscription={openProfileSubscription} />
        </header>}
        {mode === "screenwriting" && (
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <nav aria-label="Dashboard sections" className="dashboard-nav flex flex-col gap-1 border-b pb-4 lg:border-b-0 lg:border-r lg:pb-0 lg:pr-5 lg:w-[220px] lg:shrink-0">
            {dashboardSections.map(({ id, title, icon: Icon }) => (
              <Button key={id} type="text" icon={<Icon size={21} />} aria-current={section === id ? "page" : undefined} onClick={() => { setSection(id); setShowFilterModal(false); setInviteScriptId(null) }} className="w-full text-left rounded-lg text-[15px] px-3">
                {title}
              </Button>
            ))}
            {isStaff(user) && <Button type="text" icon={<MdAdminPanelSettings size={21} />} onClick={() => navigate("/admin")} className="w-full text-left rounded-lg text-[15px] px-3">Admin</Button>}
          </nav>
          <section aria-label={activeSection?.title} className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h2 className="text-[22px] font-bold">{activeSection?.title}</h2>
              {section === "scripts" && <div className="hidden lg:block"><Button className="dashboard-primary h-10 rounded-full px-4 font-bold" icon={<MdAdd size={20} />} onClick={openCreate}>Create Script</Button></div>}
            </div>
            {isScriptSection && !(section === "invite" && inviteScriptId) && <>
              <div className="flex items-center gap-3 mb-6">
                <div className="min-w-0 flex-1 max-w-[480px]"><SearchBar setSearchVal={setSearchVal} /></div>
                <Button className="dashboard-tool shrink-0 h-11 w-11 rounded-lg" aria-label="Filter scripts" title="Filter scripts" icon={<MdOutlineTune size={23} />} onClick={() => setShowFilterModal(true)} />
              </div>
              <div className="dashboard-list min-w-0">
                 {section === "shared" ? <SharedScripts itemsPerPage={12} searchVal={searchVal} setShowFilterModal={setShowFilterModal} showFilterModal={showFilterModal} /> : <AllScripts itemsPerPage={12} searchVal={searchVal} setShowCreateScriptModal={setShowCreateScriptModal} setShowFilterModal={setShowFilterModal} showFilterModal={showFilterModal} showTitlePageAction />}
              </div>
            </>}
             {section === "invite" && <>
               <div className="mb-6 max-w-[480px]">
                 <label htmlFor="invite-script-selector" className="block font-bold text-[15px] mb-2">Choose Script</label>
                 <Select id="invite-script-selector" aria-label="Choose Script" className="w-full" size="large" placeholder="Choose Script" showSearch optionFilterProp="label" loading={scriptsLoading} value={inviteScriptId || undefined} onChange={setInviteScriptId} options={inviteScripts.map(script => ({ value: script._id, label: script.title || "Untitled Script" }))} notFoundContent={scriptsLoading ? "Loading scripts…" : scriptsError ? "Could not load scripts" : "No scripts found"} />
                 {scriptsError && <div className="mt-2"><span className="dashboard-muted">Could not load your scripts. </span><Button type="link" onClick={() => setSelectorRetry(value => value + 1)}>Retry</Button></div>}
               </div>
               {inviteScriptId && <Invite key={inviteScriptId} scriptId={inviteScriptId} embeddedForm onBack={() => setInviteScriptId(null)} />}
             </>}
            {section === "profile" && <MyProfile embedded key={location.state?.profileSection === "subscription" ? "subscription" : "profile"} />}
            {section === "ai" && <AIWorkspace />}
            {company && section === "subscription" && <SubscriptionPreview />}
            {(company || teamAccess?.manager) && section === "team" && <TeamMembersPreview />}
            {company && section === "settings" && <OrganisationSettings onSaved={setOrganisation} />}
            {section === "faq" && <FAQ onBack={() => setSection("scripts")} />}
          </section>
        </div>
        )}
        {mode === "production" && canReviewProduction && (
          <ProductionPrototype openRequest={openRequest} exitLabel="Dashboard" onExit={() => setMode("screenwriting")} autoOpenId={autoOpenRef.current} onAutoOpenDone={() => { autoOpenRef.current = undefined }} openSection={autoSection} />
        )}
      </div>
      {showCreateScriptModal && <CreateScriptModal setShowModal={setShowCreateScriptModal} setType={setCreateType} />}
      <ScriptInfoModals type={createType} setType={setCreateType} />
    </div>
  )
}

export default UserDashboard
